import React from 'react';
import type { CoinData } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';

interface CoinItemProps {
  coin: CoinData;
  onClick: () => void;
  isInWatchlist?: boolean;
  onWatchlistToggle?: (coinId: string) => void;
  currency?: string;
}

export default function CoinItem({ coin, onClick, isInWatchlist = false, onWatchlistToggle, currency = 'usd' }: CoinItemProps) {
  const change = coin.price_change_percentage_24h ?? 0;
  const isPositive = change >= 0;
  const isBTC = coin.id === 'bitcoin' || coin.symbol.toLowerCase() === 'btc';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('CoinItem: Coin clicked:', coin.id, coin.name);
    onClick();
  };

  const handleWarningClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open('https://thatsbtcnotbitcoin.com', '_blank', 'noopener,noreferrer');
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onWatchlistToggle) {
      onWatchlistToggle(coin.id);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="group bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 flex items-center justify-between gap-6 transition-all duration-300 cursor-pointer shadow-lg hover:-translate-y-2 hover:shadow-bubble-lg hover:scale-[1.02] border-2 border-transparent hover:border-primary/30"
    >
      <div className="flex items-center gap-5 flex-1">
        {coin.image && (
          <div className="relative">
            <img
              src={coin.image}
              alt={coin.name}
              className="w-16 h-16 rounded-2xl object-cover ring-4 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300 shadow-lg"
              onError={(e) => {
                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2264%22 height=%2264%22%3E%3Crect fill=%22%236366f1%22 width=%2264%22 height=%2264%22 rx=%2216%22/%3E%3C/svg%3E';
              }}
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-primary to-primary-light rounded-full border-4 border-bg-secondary shadow-lg"></div>
          </div>
        )}
        <div className="flex-1 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-xl font-bold mb-1.5 text-text group-hover:text-primary transition-colors">{coin.name}</div>
            <div className="text-text-secondary text-sm font-medium uppercase tracking-wider">{coin.symbol}</div>
          </div>
          <div className="flex items-center gap-2">
            {onWatchlistToggle && (
              <button
                onClick={handleStarClick}
                className={`text-2xl transition-all duration-300 hover:scale-110 active:scale-95 flex-shrink-0 ${
                  isInWatchlist 
                    ? 'text-yellow-400 hover:text-yellow-300' 
                    : 'text-text-secondary hover:text-yellow-400'
                }`}
                title={isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist (50 sats)'}
              >
                {isInWatchlist ? '⭐' : '☆'}
              </button>
            )}
            {isBTC && (
              <a
                href="https://thatsbtcnotbitcoin.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWarningClick}
                className="text-yellow-400 hover:text-yellow-300 text-xl transition-all duration-300 hover:scale-110 active:scale-95 flex-shrink-0"
                title="That's BTC, not Bitcoin"
              >
                ⚠️
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl font-bold mb-2 bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(coin.current_price || 0, currency, { compact: true })}</div>
        <div
          className={`text-sm font-bold px-4 py-2 rounded-full inline-block ${
            isPositive
              ? 'text-green-400 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 shadow-glow-green'
              : 'text-red-400 bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30'
          }`}
        >
          {coin.price_change_percentage_24h != null
            ? `${isPositive ? '↗' : '↘'} ${isPositive ? '+' : ''}${change.toFixed(2)}%`
            : 'N/A'}
        </div>
      </div>
    </div>
  );
}

